/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useReducer } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StoreFilter } from '@/components/StoreFilter';
import { usePlatform } from '@/hooks/usePlatform';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2, Sparkles, Plus, Wand2, Link2, Check, AlertTriangle, X, Trash2, Save, RotateCcw, Layers,
} from 'lucide-react';
import { toast } from 'sonner';

function ResultPanel({ result }) {
  if (!result) return null;
  const s = result.summary || {};
  return (
    <Card className="card-flat p-6 mt-6">
      <div className="overline mb-3">— Résultat de la synchro</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-md">
          <div className="text-xs text-zinc-500">Matchés</div>
          <div className="font-mono text-2xl font-bold text-emerald-700">{s.matched_count || 0}</div>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-md">
          <div className="text-xs text-zinc-500">Créés</div>
          <div className="font-mono text-2xl font-bold">{s.created || 0}</div>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-md">
          <div className="text-xs text-zinc-500">Mis à jour</div>
          <div className="font-mono text-2xl font-bold">{s.updated || 0}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
          <div className="text-xs text-zinc-500">Non matchés</div>
          <div className="font-mono text-2xl font-bold text-amber-700">{s.unmatched_shopify_count || 0}</div>
        </div>
      </div>
      {result.unmatched_shopify?.length > 0 && (
        <div className="mt-3 text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-md p-3">
          <strong>{result.unmatched_shopify.length} produit(s) Shopify sans vitrine.</strong> Utilisez l'onglet
          "Lier manuellement" ci-dessous pour les associer.
        </div>
      )}
    </Card>
  );
}

function ManualLinkDialog({ product, open, onOpenChange, onSaved }) {
  const hasVariants = (product?.variants?.length || 0) > 1;
  const [form, setForm] = useState(() => ({
    vitrine_name: product?.vitrine_name || product?.name || '',
    vitrine_url: product?.vitrine_url || '',
    vitrine_description: product?.vitrine_description || '',
    vitrine_image_url: product?.vitrine_image_url || product?.image_url || '',
  }));
  const [vrows, setVrows] = useState(() => (product?.variants || []).map((v) => ({
    variant_id: String(v.variant_id || ''),
    title: v.title || '',
    price: v.price,
    vitrine_name: v.vitrine_name || '',
    vitrine_url: v.vitrine_url || '',
    vitrine_image_url: v.vitrine_image_url || '',
  })));
  const setVrow = (i, k, val) => setVrows((r) => r.map((x, j) => (j === i ? { ...x, [k]: val } : x)));

  const save = async () => {
    try {
      if (hasVariants) {
        await api.put(`/products/${product.id}/variants`, {
          variants: vrows.map((r) => ({
            variant_id: r.variant_id, vitrine_name: r.vitrine_name,
            vitrine_url: r.vitrine_url, vitrine_image_url: r.vitrine_image_url,
          })),
        });
      } else {
        await api.post('/sync/manual-link', { product_id: product.id, ...form });
      }
      toast.success('Produit lié');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={hasVariants ? 'max-w-3xl' : ''}>
        <DialogHeader>
          <DialogTitle>Lier vitrine {hasVariants ? '— par variante' : 'au produit Shopify'}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 mb-3">
          {product.platform === 'woocommerce' ? 'WooCommerce' : 'Shopify'} : <span className="font-mono">{product.name}</span>
          {!hasVariants && <> — {product.price}€</>}
        </div>
        {hasVariants ? (
          <div className="max-h-[55vh] overflow-auto -mx-1 px-1">
            <table className="w-full text-sm" data-testid="manual-variants-table">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-xs text-zinc-400 uppercase tracking-wider border-b border-zinc-100">
                  <th className="py-2 pr-3 font-medium">Variante</th>
                  <th className="py-2 pr-3 font-medium">Prix</th>
                  <th className="py-2 pr-3 font-medium">Nom vitrine</th>
                  <th className="py-2 font-medium">URL vitrine</th>
                </tr>
              </thead>
              <tbody>
                {vrows.map((r, i) => (
                  <tr key={r.variant_id || i} className={`border-b border-zinc-50 ${r.vitrine_name.trim() ? '' : 'bg-amber-50/60'}`}>
                    <td className="py-2 pr-3 font-medium text-zinc-700 truncate max-w-[130px]" title={r.title}>{r.title || '—'}</td>
                    <td className="py-2 pr-3 font-mono font-bold whitespace-nowrap">{Number(r.price).toFixed(2)} €</td>
                    <td className="py-2 pr-3"><Input value={r.vitrine_name} onChange={(e) => setVrow(i, 'vitrine_name', e.target.value)} placeholder="Nom vitrine" className="h-9 rounded-md text-sm w-40" data-testid={`manual-variant-name-${i}`} /></td>
                    <td className="py-2"><Input value={r.vitrine_url} onChange={(e) => setVrow(i, 'vitrine_url', e.target.value)} placeholder="https://…" className="h-9 rounded-md text-sm w-44" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom du pack vitrine</Label>
              <Input value={form.vitrine_name} onChange={(e) => setForm({ ...form, vitrine_name: e.target.value })} data-testid="manual-link-name" className="mt-1 h-10 rounded-md" />
            </div>
            <div>
              <Label className="text-xs">URL de la page vitrine</Label>
              <Input value={form.vitrine_url} onChange={(e) => setForm({ ...form, vitrine_url: e.target.value })} data-testid="manual-link-url" className="mt-1 h-10 rounded-md font-mono text-xs" placeholder="https://vitrine.com/produit-x" />
            </div>
            <div>
              <Label className="text-xs">URL image (optionnel)</Label>
              <Input value={form.vitrine_image_url} onChange={(e) => setForm({ ...form, vitrine_image_url: e.target.value })} className="mt-1 h-10 rounded-md font-mono text-xs" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">Annuler</Button>
          <Button onClick={save} data-testid="manual-link-save" className="btn-primary rounded-md">Lier</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Sync() {
  const { matchStore, label: platformLabel } = usePlatform();
  const [result, setResult] = useState(null);
  const [shopifyProducts, dispatchProducts] = useReducer((_s, a) => a, []);
  const [linkProduct, setLinkProduct] = useState(null);

  // Matching scope (per-store / per-vitrine segmentation)
  const [stores, setStores] = useState([]);
  const [syncStoreId, setSyncStoreId] = useState('');     // '' = toutes les boutiques
  const [syncStatus, setSyncStatus] = useState([]);       // pool vitrines + snapshot status
  const [targetVitrineId, setTargetVitrineId] = useState(''); // vitrine ciblée pour la synchro

  // Manual entry tab
  const [manualItems, setManualItems] = useState([{ name: '', url: '', price: '', image_url: '' }]);
  const [manualLoading, setManualLoading] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);

  // Scrape tab
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapedProducts, setScrapedProducts] = useState([]);

  const loadProducts = async () => {
    try {
      const r = await api.get('/products');
      dispatchProducts(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadProducts(); }, []); // eslint-disable-line

  useEffect(() => {
    api.get('/stores').then((r) => setStores(r.data)).catch(() => {});
  }, []);

  // Load the Batmobile pool sync-readiness for the selected store, and default the
  // target vitrine to the currently active one.
  useEffect(() => {
    if (!syncStoreId) { setSyncStatus([]); setTargetVitrineId(''); return; }
    let active = true;
    api.get(`/stores/${syncStoreId}/rotation/sync-status`)
      .then((r) => {
        if (!active) return;
        const vits = r.data.vitrines || [];
        setSyncStatus(vits);
        const cur = vits.find((v) => v.is_active) || vits[0];
        setTargetVitrineId(cur?.vitrine_id || '');
      })
      .catch(() => { if (active) { setSyncStatus([]); setTargetVitrineId(''); } });
    return () => { active = false; };
  }, [syncStoreId]);

  // Load the saved manual-entry draft for the selected store + vitrine (or global).
  useEffect(() => {
    let active = true;
    api.get('/sync/draft', { params: { store_id: syncStoreId || '', vitrine_id: targetVitrineId || '' } })
      .then((r) => {
        if (!active) return;
        const it = r.data.items || [];
        setManualItems(it.length ? it : [{ name: '', url: '', price: '', image_url: '' }]);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [syncStoreId, targetVitrineId]);

  const scopedStores = stores.filter(matchStore);
  const platformStoreIds = new Set(stores.filter(matchStore).map((s) => s.id));
  const targetVitrine = syncStatus.find((v) => v.vitrine_id === targetVitrineId);

  const saveDraft = async (silent = false) => {
    setDraftSaving(true);
    try {
      const items = manualItems.filter((it) => it.name || it.url || it.price || it.image_url);
      await api.put('/sync/draft', { store_id: syncStoreId, vitrine_id: targetVitrineId, items });
      if (!silent) toast.success('Brouillon enregistré');
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setDraftSaving(false);
    }
  };

  const resetDraft = async () => {
    if (!window.confirm('Réinitialiser la saisie de cette synchro ? Les données saisies seront effacées.')) return;
    try {
      await api.delete('/sync/draft', { params: { store_id: syncStoreId || '', vitrine_id: targetVitrineId || '' } });
      setManualItems([{ name: '', url: '', price: '', image_url: '' }]);
      toast.success('Données de synchro réinitialisées');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const refreshStatus = async () => {
    if (!syncStoreId) return;
    try {
      const r = await api.get(`/stores/${syncStoreId}/rotation/sync-status`);
      setSyncStatus(r.data.vitrines || []);
    } catch (e) { /* silent */ }
  };

  const runSync = async (catalog) => {
    try {
      const r = await api.post('/sync/manual', { catalog, store_id: syncStoreId, vitrine_id: targetVitrineId });
      setResult(r.data);
      toast.success(`Sync terminée : ${r.data.summary?.matched_count || 0} matché(s)`);
      await loadProducts();
      await refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur sync');
    }
  };

  const submitManual = async () => {
    const valid = manualItems
      .map((it) => ({ ...it, price: parseFloat(it.price) }))
      .filter((it) => it.name.trim() && it.url.trim() && it.price > 0)
      .map((it, i) => ({ id: it.url || `manual-${i}`, name: it.name.trim(), url: it.url.trim(), price: it.price, image_url: it.image_url.trim() }));
    if (!valid.length) { toast.error('Ajoutez au moins 1 produit avec nom + URL + prix'); return; }
    setManualLoading(true);
    await saveDraft(true); // persist the typed catalog so it survives next visit
    await runSync(valid);
    setManualLoading(false);
  };

  const submitScrape = async () => {
    if (!scrapeUrl.startsWith('http')) { toast.error('URL invalide'); return; }
    setScrapeLoading(true);
    try {
      const r = await api.post('/sync/scrape', { url: scrapeUrl });
      setScrapedProducts(r.data.products || []);
      if (!r.data.products?.length) toast.warning('Aucun produit détecté (la page n\'expose pas JSON-LD)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur scrape');
    } finally {
      setScrapeLoading(false);
    }
  };

  const syncScrapedProducts = async () => {
    if (!scrapedProducts.length) return;
    await runSync(scrapedProducts);
  };

  const unmatched = shopifyProducts.filter((p) => !p.vitrine_name && platformStoreIds.has(p.store_id) && (!syncStoreId || p.store_id === syncStoreId));

  const [bulkLinking, setBulkLinking] = useState(false);
  const bulkLink = async () => {
    if (!unmatched.length) return;
    if (!window.confirm(`Lier automatiquement ${unmatched.length} produit(s) en reprenant leur nom et image Shopify comme infos vitrine ?`)) return;
    setBulkLinking(true);
    try {
      const r = await api.post('/sync/manual-link-bulk', {
        product_ids: unmatched.map((p) => p.id),
        store_id: syncStoreId || '',
      });
      toast.success(`${r.data.linked || 0} produit(s) lié(s) en masse`);
      await loadProducts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur synchro de masse');
    } finally {
      setBulkLinking(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="overline mb-2">— Sync vitrine ↔ Shopify</div>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Synchronisation produits{platformLabel ? ` · ${platformLabel}` : ''}</h1>
        <p className="text-zinc-600 mt-2">
          Liez vos packs vitrine à vos produits Shopify. Le match se fait par prix (auto), avec édition manuelle possible.
        </p>
      </div>

      {/* Matching scope — boutique + vitrine cible (compatible Batmobile) */}
      <Card className="card-flat p-6 mb-6" data-testid="sync-scope-card">
        <div className="overline mb-2">— Cible du matching</div>
        <p className="text-sm text-zinc-600 mb-4">
          Sélectionnez la <strong>boutique</strong>, puis la <strong>vitrine</strong> du pool Batmobile à préparer.
          Chaque vitrine garde son propre catalogue/prix : synchronisez-les une par une pour que la rotation Stripe
          envoie toujours les bonnes métadonnées au compte concerné. La vitrine active n'est jamais perturbée quand
          vous préparez une autre vitrine.
        </p>
        <div className="flex flex-wrap gap-4">
          <div>
            <Label className="text-xs mb-1.5 block">Boutique</Label>
            <StoreFilter stores={scopedStores} value={syncStoreId} onChange={setSyncStoreId} testId="sync-store-filter" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Vitrine cible</Label>
            <Select
              value={targetVitrineId || '__none__'}
              onValueChange={(v) => setTargetVitrineId(v === '__none__' ? '' : v)}
              disabled={!syncStoreId || syncStatus.length === 0}
            >
              <SelectTrigger data-testid="sync-target-vitrine" className="h-10 w-72 rounded-md">
                <SelectValue placeholder={syncStoreId ? 'Choisir une vitrine' : 'Sélectionnez une boutique'} />
              </SelectTrigger>
              <SelectContent>
                {syncStatus.map((v) => (
                  <SelectItem key={v.vitrine_id} value={v.vitrine_id}>
                    {v.vitrine_name}{v.is_active ? ' · active' : ''}{v.has_snapshot ? ' ✅' : ' ⚠️'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!syncStoreId ? (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="sync-scope-note">
            ⚠️ Aucune boutique sélectionnée — le matching s'appliquera à <strong>TOUTES</strong> les boutiques.
          </div>
        ) : (
          <>
            {targetVitrine && (
              <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2.5" data-testid="sync-scope-note">
                ✅ Synchro ciblée : <strong>{stores.find((s) => s.id === syncStoreId)?.name}</strong> →
                vitrine <strong>{targetVitrine.vitrine_name}</strong>
                {!targetVitrine.is_active && <> (préparation hors-ligne — la vitrine active reste intacte)</>}
              </div>
            )}
            {/* Pool sync-readiness panel */}
            {syncStatus.length > 0 && (
              <div className="mt-4" data-testid="sync-pool-status">
                <div className="overline mb-2 text-[11px]">— État du pool Batmobile</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {syncStatus.map((v) => (
                    <button
                      key={v.vitrine_id}
                      type="button"
                      onClick={() => setTargetVitrineId(v.vitrine_id)}
                      data-testid={`sync-pool-vitrine-${v.vitrine_id}`}
                      className={`text-left border rounded-md p-3 transition-colors ${
                        v.vitrine_id === targetVitrineId ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{v.vitrine_name}</span>
                        {v.is_active && <span className="text-[10px] uppercase tracking-wider bg-zinc-900 text-white px-1.5 py-0.5 rounded">active</span>}
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                        {v.has_snapshot ? (
                          <span className="text-emerald-700 flex items-center gap-1"><Check className="h-3 w-3" /> {v.mapped_count} produit(s) mappé(s)</span>
                        ) : (
                          <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> jamais synchronisée</span>
                        )}
                      </div>
                      {!v.catalog_url && (
                        <div className="mt-1 text-[10px] text-zinc-400">Pas d'URL catalogue — utilisez la saisie manuelle</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="mb-4 grid grid-cols-3 max-w-2xl">
          <TabsTrigger value="manual" data-testid="sync-tab-manual" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Saisie manuelle
          </TabsTrigger>
          <TabsTrigger value="scrape" data-testid="sync-tab-scrape" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Détection auto (URL)
          </TabsTrigger>
          <TabsTrigger value="link" data-testid="sync-tab-link" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <Link2 className="h-3.5 w-3.5 mr-1.5" /> Lier manuellement
          </TabsTrigger>
        </TabsList>

        {/* TAB A: Manual entry */}
        <TabsContent value="manual">
          <Card className="card-flat p-6">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="h-5 w-5 text-zinc-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display font-bold text-lg">Saisis tes packs vitrine</h2>
                <p className="text-xs text-zinc-600 mt-1">Le matching avec Shopify se fait par prix. Indique le prix final que paie ton client (livraison incluse).</p>
              </div>
            </div>
            <div className="space-y-3">
              {manualItems.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`manual-row-${i}`}>
                  <Input className="col-span-4 h-10 rounded-md" placeholder="Nom du pack" value={it.name} onChange={(e) => { const a = [...manualItems]; a[i].name = e.target.value; setManualItems(a); }} />
                  <Input className="col-span-4 h-10 rounded-md font-mono text-xs" placeholder="URL vitrine" value={it.url} onChange={(e) => { const a = [...manualItems]; a[i].url = e.target.value; setManualItems(a); }} />
                  <Input className="col-span-2 h-10 rounded-md font-mono" placeholder="29.99" type="number" step="0.01" value={it.price} onChange={(e) => { const a = [...manualItems]; a[i].price = e.target.value; setManualItems(a); }} />
                  <Input className="col-span-1 h-10 rounded-md font-mono text-xs" placeholder="Image" value={it.image_url} onChange={(e) => { const a = [...manualItems]; a[i].image_url = e.target.value; setManualItems(a); }} />
                  <Button variant="ghost" size="sm" className="col-span-1 h-10 p-0" onClick={() => setManualItems(manualItems.filter((_, j) => j !== i))} disabled={manualItems.length === 1}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setManualItems([...manualItems, { name: '', url: '', price: '', image_url: '' }])} data-testid="manual-add-row" className="rounded-md h-9 text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un pack
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-5">
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => saveDraft(false)} disabled={draftSaving} data-testid="sync-save-draft" className="rounded-md h-9 text-xs">
                  {draftSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Enregistrer le brouillon
                </Button>
                <Button variant="ghost" onClick={resetDraft} data-testid="sync-reset-draft" className="rounded-md h-9 text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Réinitialiser
                </Button>
              </div>
              <Button onClick={submitManual} disabled={manualLoading} data-testid="manual-submit" className="btn-primary rounded-md">
                {manualLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Synchroniser avec Shopify
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3" data-testid="sync-draft-hint">
              💾 Ta saisie est <strong>sauvegardée automatiquement</strong> à chaque synchro (et via « Enregistrer le brouillon »)
              {syncStoreId ? ' pour cette boutique' : ' (global)'} — tu la retrouveras à ta prochaine visite. Le bouton « Réinitialiser » efface la saisie.
            </p>
          </Card>
        </TabsContent>

        {/* TAB B: Scrape from URL */}
        <TabsContent value="scrape">
          <Card className="card-flat p-6">
            <div className="flex items-start gap-3 mb-4">
              <Wand2 className="h-5 w-5 text-zinc-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display font-bold text-lg">Détection automatique</h2>
                <p className="text-xs text-zinc-600 mt-1">
                  Colle l'URL d'une page vitrine (catalogue OU page produit). Nous extrayons automatiquement les produits via le schéma JSON-LD (universel).
                </p>
              </div>
            </div>
            <div className="flex gap-2 mb-4">
              <Input value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)} placeholder="https://votre-vitrine.com/pack-1" data-testid="scrape-url-input" className="h-11 rounded-md font-mono flex-1" />
              <Button onClick={submitScrape} disabled={scrapeLoading} data-testid="scrape-submit" className="btn-primary rounded-md h-11">
                {scrapeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Détecter
              </Button>
            </div>
            {scrapedProducts.length > 0 && (
              <>
                <div className="mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2.5 flex items-center gap-2">
                  <Check className="h-3.5 w-3.5" /> {scrapedProducts.length} produit(s) détecté(s) — vérifie puis lance la sync
                </div>
                <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                  {scrapedProducts.map((p, i) => (
                    <div key={p.url || `scraped-${i}`} className="flex items-center gap-3 border border-zinc-200 rounded-md p-2 text-sm">
                      {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{p.url}</div>
                      </div>
                      <div className="font-mono text-sm font-bold">{p.price}€</div>
                      <Button size="sm" variant="ghost" onClick={() => setScrapedProducts(scrapedProducts.filter((_, j) => j !== i))}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button onClick={syncScrapedProducts} data-testid="scrape-sync" className="btn-primary rounded-md">
                    Synchroniser {scrapedProducts.length} produit(s) avec Shopify
                  </Button>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        {/* TAB C: Manual link */}
        <TabsContent value="link">
          <Card className="card-flat p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <Link2 className="h-5 w-5 text-zinc-400 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display font-bold text-lg">Produits Shopify sans vitrine</h2>
                <p className="text-xs text-zinc-600 mt-1">
                  Lie manuellement un produit Shopify à un pack vitrine quand le matching automatique a manqué.
                </p>
              </div>
            </div>
            {unmatched.length > 0 && (
              <Button onClick={bulkLink} disabled={bulkLinking} data-testid="sync-bulk-link" className="btn-primary rounded-md h-9 text-xs shrink-0">
                {bulkLinking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Layers className="h-3.5 w-3.5 mr-1.5" />}
                Synchronisation de masse ({unmatched.length})
              </Button>
            )}
          </div>
            {unmatched.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-500">
                <Check className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                Tous les produits Shopify sont liés à une vitrine ✨
              </div>
            ) : (
              <div className="space-y-2">
                {unmatched.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 border border-amber-200 bg-amber-50 rounded-md p-3" data-testid={`unmatched-${p.id}`}>
                    {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate text-sm">{p.name}</div>
                      <div className="text-xs text-zinc-500 truncate">SKU: {p.sku || '—'} · {p.price}€</div>
                    </div>
                    <Button size="sm" onClick={() => setLinkProduct(p)} className="btn-primary rounded-md h-8 text-xs">
                      <Link2 className="h-3 w-3 mr-1" /> Lier
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <ResultPanel result={result} />

      <ManualLinkDialog
        key={linkProduct?.id || 'closed'}
        product={linkProduct}
        open={!!linkProduct}
        onOpenChange={(o) => !o && setLinkProduct(null)}
        onSaved={loadProducts}
      />
    </div>
  );
}
